// ────────────────────────────────────────────────────────────────────────────
// Everything needed to render an invoice through the Document Builder.
//
// One hook because the two places that produce a PDF — generation (Create
// invoices) and download (Invoice summary) — must resolve the *same* template,
// organisation block and bank account. Two copies of this logic would drift,
// and the symptom would be an invoice that looks different depending on which
// button produced it.
//
// Three things it assembles that the old hardcoded PDF never had:
//   * the organisation's default template for the document's kind,
//   * the full organisation block (address lines, VAT and registration
//     numbers, and the logo as a data URI — a storage SAS URL cannot be used,
//     see `useOrganisationLogoDataUri`),
//   * the bank account matching the invoice's currency.
// ────────────────────────────────────────────────────────────────────────────
import {
  useAddress,
  useOrganisation,
  useOrganisationBankAccounts,
  useOrganisationLogoDataUri,
} from '@my/directory-api/hooks';
import type { Invoice, InvoiceDocTypeValue } from '@my/tms-api';
import { useMemo } from 'react';
import { selectBankAccount } from '@/utils/bankAccounts';
import {
  invoiceToDocumentData,
  splitAddress,
  type DocumentData,
  type DocumentLineContext,
  type DocumentOrganisation,
} from '@/features/settings/documents/lib/documentData';
import { readLayoutPref } from '@/utils/documentLayoutPrefs';
import { selectDefaultOf } from '@/features/settings/documents/lib/bands';
import { useDocumentTemplates } from '@/features/settings/documents/lib/useDocumentTemplates';
import type { DocumentTemplate, DocumentType } from '@/features/settings/documents/lib/types';

/**
 * The layout preferences carried by a customer — the stored
 * `{ app: { kind: layoutKey } }` map. A structural type rather than the whole
 * `Party`, so a caller holding nothing but the map does not have to invent one.
 */
export interface PartyLayoutPreferences {
  preferredDocumentLayoutsJson?: string | null;
}

/**
 * Which template kind prints a given invoice.
 *
 * A pro forma is its own kind: it must be marked as not being a VAT invoice and
 * carries no due date, so it cannot share the invoice layout. An ad-hoc invoice
 * is an ordinary invoice that simply has no order behind it, so it does.
 */
export function templateKindFor(docType: InvoiceDocTypeValue): DocumentType {
  if (docType === 'Credit') return 'CreditNote';
  if (docType === 'Proforma') return 'Proforma';
  return 'Invoice';
}

export interface InvoiceDocumentSource {
  /** The organisation block, ready for the renderer. */
  organisation: DocumentOrganisation;
  /**
   * The layout to print this document with.
   *
   * Pass the CUSTOMER and let this pick, rather than passing a key: the choice
   * lives in a per-app, per-kind map, and a caller that reads the wrong entry
   * prints a document in a layout nobody chose. The choice is resolved
   * **leniently** — a key matching no live layout of this kind falls back to the
   * organisation default, which is a normal outcome rather than an error,
   * because the key is not a foreign key and layouts are soft-deleted.
   */
  templateFor: (
    docType: InvoiceDocTypeValue,
    customer?: PartyLayoutPreferences | null,
  ) => DocumentTemplate | undefined;
  /**
   * Full render payload for one invoice, bank account resolved by currency.
   *
   * `contextByOrderId` supplies the transport detail (dates, route, the order's
   * references, cargo) for the line table's optional columns. It is attached to
   * every line of the order; the renderer decides which row prints it.
   */
  buildData: (
    invoice: Invoice,
    contextByOrderId?: Map<string, DocumentLineContext>,
  ) => DocumentData;
  /** False while the organisation is still loading — render would miss its letterhead. */
  ready: boolean;
}

export function useInvoiceDocumentSource(organisationId: string | null): InvoiceDocumentSource {
  const orgQuery = useOrganisation(organisationId);
  const org = orgQuery.data;
  const addressQuery = useAddress(org?.mainOfficeAddressId ?? null);
  const banksQuery = useOrganisationBankAccounts(organisationId);
  const logoQuery = useOrganisationLogoDataUri(organisationId, Boolean(org?.logo));
  const { templates } = useDocumentTemplates(organisationId);

  const organisation = useMemo<DocumentOrganisation>(
    () => ({
      // `invoicingName` is the legal name for documents; `name` is the
      // day-to-day one. The old PDF printed `name`, which was a bug for any org
      // that set them differently.
      name: org?.invoicingName || org?.name || '',
      addressLines: splitAddress(addressQuery.data?.fullAddress),
      taxNumber: org?.taxNumber ?? null,
      registrationNumber: org?.registrationNumber ?? null,
      phone: null,
      email: null,
      website: null,
      logoUrl: logoQuery.data ?? null,
    }),
    [org, addressQuery.data, logoQuery.data],
  );

  return useMemo(
    () => ({
      organisation,
      ready: Boolean(org),
      templateFor: (docType, customer) => {
        const kind = templateKindFor(docType);
        const forKind = templates.filter((t) => t.docType === kind);
        const key = readLayoutPref(customer?.preferredDocumentLayoutsJson, kind);
        const preferred = key ? forKind.find((t) => t.id === key) : undefined;
        return preferred ?? selectDefaultOf(forKind);
      },
      buildData: (invoice, contextByOrderId) => {
        const account = selectBankAccount(banksQuery.data, invoice.currencyIsoCode).account;
        const data = invoiceToDocumentData(
          invoice,
          organisation,
          account
            ? {
                accountName: account.name,
                bankName: account.bankName,
                iban: account.iban,
                bic: account.bic,
                sortCode: account.sortCode,
                accountNumber: account.accountNumber,
              }
            : undefined,
        );
        if (!contextByOrderId?.size) return data;

        // Every line of an order gets its context. Which cells print is the
        // renderer's call (`markContinuations` shows them on the order's first
        // line only), and attaching to all lines means the order's date and
        // reference still print when the transport charge has been excluded —
        // they belong to the order, not to any one charge.
        return {
          ...data,
          lines: data.lines.map((line) => {
            const context = line.orderId ? contextByOrderId.get(line.orderId) : undefined;
            return context ? { ...line, context } : line;
          }),
        };
      },
    }),
    [organisation, org, templates, banksQuery.data],
  );
}
